import { Link, getRouteApi } from '@tanstack/react-router'
import { ArrowLeft, MapPin, Star } from 'lucide-react'

import { ShareButton } from '@/components/share'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { CourseCard } from '@/components/course-card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useCourses } from '@/features/catalog/api'
import { PortfolioCarousel } from '@/components/portfolio-carousel'
import { useExpert } from '@/features/marketplace/api'
import { formatNumber, formatRupiah } from '@/lib/format'

const route = getRouteApi('/public/talenta/$expertId')

export function TalentDetailPage() {
  const { expertId } = route.useParams()
  const { data: expert, isLoading } = useExpert(expertId)
  // Katalog disaring di klien karena penyaringan berdasarkan pengajar belum
  // tersedia pada endpoint publik; jumlah kelas per pengajar masih kecil.
  const { data: courses } = useCourses({ sort: 'popular' })

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    )
  }

  if (!expert) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          className="card"
          title="Profil tidak ditemukan"
          description="Tenaga ahli ini mungkin sudah tidak aktif."
          action={
            <Link to="/talenta">
              <Button variant="outline">Kembali ke direktori</Button>
            </Link>
          }
        />
      </div>
    )
  }

  const taught = courses?.courses.filter((course) => course.expert.id === expertId) ?? []

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <Link
        to="/talenta"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Direktori talenta
      </Link>

      <Card className="mt-4">
        <CardBody className="flex flex-col gap-6 sm:flex-row">
          <Avatar name={expert.full_name} src={expert.avatar_url || undefined} size="xl" />

          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-extrabold tracking-tight text-ink">{expert.full_name}</h1>
            <p className="mt-1 text-muted">{expert.headline || 'Tenaga ahli Skava'}</p>

            <div className="mt-4">
              <ShareButton title={expert.full_name} text={`${expert.full_name} di skava.id`} />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted">
              {expert.city ? (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="size-4" />
                  {expert.city}
                </span>
              ) : null}
              {expert.rating_count > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <Star className="size-4 fill-amber-400 text-amber-400" />
                  {expert.rating.toFixed(1)} dari {formatNumber(expert.rating_count)} ulasan
                </span>
              ) : null}
              <span>{formatNumber(expert.projects_done)} proyek selesai</span>
              {expert.hourly_rate ? <span>{formatRupiah(expert.hourly_rate)} per jam</span> : null}
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {expert.skills.map((skill) => (
                <Badge key={skill}>{skill}</Badge>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      {expert.bio ? (
        <Card className="mt-6">
          <CardHeader title="Tentang" />
          <CardBody>
            <p className="whitespace-pre-line leading-relaxed text-muted">{expert.bio}</p>
          </CardBody>
        </Card>
      ) : null}

      {expert.portfolios?.length ? (
        <section className="mt-8">
          <div className="flex items-end justify-between gap-4">
            <h2 className="text-xl font-extrabold tracking-tight text-ink">Portofolio</h2>
            <p className="text-sm text-muted">{expert.portfolios.length} karya</p>
          </div>
          <PortfolioCarousel items={expert.portfolios} className="mt-4" />
        </section>
      ) : null}

      {taught.length > 0 ? (
        <section className="mt-8">
          <h2 className="text-xl font-extrabold tracking-tight text-ink">Kelas yang diampu</h2>
          <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {taught.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        </section>
      ) : null}

      <Card className="mt-8">
        <CardBody className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-bold text-ink">Ingin bekerja dengan {expert.full_name.split(' ')[0]}?</p>
            <p className="mt-0.5 text-sm text-muted">
              Posting kebutuhan Anda di papan proyek, lalu terima proposal beserta rencana pengerjaannya.
            </p>
          </div>
          <Link to="/app/proyek/baru">
            <Button>Posting proyek</Button>
          </Link>
        </CardBody>
      </Card>
    </div>
  )
}
